import {
  collection, doc, addDoc, updateDoc, deleteDoc,
  query, where, Timestamp,
} from 'firebase/firestore';
import { db } from './config';
import { withTimeout, queryCacheFirst } from './utils';
import { PMSchedule, WorkOrder } from '../models/types';



// ─── PM Schedules ─────────────────────────────────────────────────────────

const PM_SCHEDULES = 'pmSchedules';

function mapSchedule(id: string, data: Record<string, any>): PMSchedule {
  return {
    id,
    projectId: data.projectId ?? '',
    assetDescription: data.assetDescription ?? '',
    assetLocation: data.assetLocation ?? '',
    taskName: data.taskName ?? '',
    trade: data.trade ?? '',
    frequencyValue: data.frequencyValue ?? 1,
    frequencyUnit: data.frequencyUnit ?? 'months',
    estimatedHours: data.estimatedHours ?? 1,
    complianceRequired: data.complianceRequired ?? false,
    complianceStandard: data.complianceStandard ?? undefined,
    lastCompletedDate: data.lastCompletedDate ?? undefined,
    nextDueDate: data.nextDueDate?.toDate?.()?.toISOString?.()?.slice(0, 10) ?? data.nextDueDate ?? '',
    status: data.status ?? 'active',
    notes: data.notes ?? undefined,
    createdAt: data.createdAt?.toDate?.()?.toISOString() ?? data.createdAt ?? '',
  };
}

export const createPMSchedule = async (schedule: Omit<PMSchedule, 'id' | 'createdAt'>): Promise<string> => {
  const now = Timestamp.now();
  const ref = await withTimeout(addDoc(collection(db, PM_SCHEDULES), {
    ...schedule,
    createdAt: now,
  }));
  return ref.id;
};

/** Cache-first; `onRefresh` (optional) gets the fresh server copy in the background. */
export const getPMSchedules = (projectId: string, onRefresh?: (s: PMSchedule[]) => void): Promise<PMSchedule[]> =>
  queryCacheFirst(query(collection(db, PM_SCHEDULES), where('projectId', '==', projectId)), snap =>
    snap.docs.map(d => mapSchedule(d.id, d.data())).sort((a, b) => a.nextDueDate.localeCompare(b.nextDueDate)), onRefresh);

export const updatePMSchedule = async (id: string, partial: Partial<Omit<PMSchedule, 'id' | 'projectId' | 'createdAt'>>): Promise<void> => {
  await withTimeout(updateDoc(doc(db, PM_SCHEDULES, id), partial));
};

export const deletePMSchedule = async (id: string): Promise<void> => {
  await withTimeout(deleteDoc(doc(db, PM_SCHEDULES, id)));
};

// ─── Work Orders ──────────────────────────────────────────────────────────

const WORK_ORDERS = 'workOrders';

function mapWorkOrder(id: string, data: Record<string, any>): WorkOrder {
  return {
    id,
    projectId: data.projectId ?? '',
    scheduleId: data.scheduleId ?? undefined,
    assetDescription: data.assetDescription ?? undefined,
    assetLocation: data.assetLocation ?? undefined,
    type: data.type ?? 'reactive',
    priority: data.priority ?? 'P3',
    status: data.status ?? 'open',
    title: data.title ?? '',
    description: data.description ?? '',
    trade: data.trade ?? '',
    assignedTo: data.assignedTo ?? undefined,
    dueDate: data.dueDate ?? undefined,
    completedDate: data.completedDate ?? undefined,
    laborHours: data.laborHours ?? undefined,
    resolution: data.resolution ?? undefined,
    notes: data.notes ?? undefined,
    createdAt: data.createdAt?.toDate?.()?.toISOString() ?? data.createdAt ?? '',
    updatedAt: data.updatedAt?.toDate?.()?.toISOString() ?? data.updatedAt ?? '',
  };
}

export const createWorkOrder = async (wo: Omit<WorkOrder, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  const now = Timestamp.now();
  const ref = await withTimeout(addDoc(collection(db, WORK_ORDERS), {
    ...wo,
    createdAt: now,
    updatedAt: now,
  }));
  return ref.id;
};

export const getWorkOrders = (projectId: string, onRefresh?: (w: WorkOrder[]) => void): Promise<WorkOrder[]> =>
  queryCacheFirst(query(collection(db, WORK_ORDERS), where('projectId', '==', projectId)), snap =>
    snap.docs.map(d => mapWorkOrder(d.id, d.data())).sort((a, b) => b.createdAt.localeCompare(a.createdAt)), onRefresh);

export const updateWorkOrder = async (id: string, partial: Partial<Omit<WorkOrder, 'id' | 'projectId' | 'createdAt'>>): Promise<void> => {
  await withTimeout(updateDoc(doc(db, WORK_ORDERS, id), { ...partial, updatedAt: Timestamp.now() }));
};

export const deleteWorkOrder = async (id: string): Promise<void> => {
  await withTimeout(deleteDoc(doc(db, WORK_ORDERS, id)));
};
